"use client"

import {UiErrorText} from "@/components/i18n/ui-error-text"
import {localizeConversationDisplay} from "@/lib/i18n/conversation-copy"
import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { ExternalLink } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { PageLoader } from "@/components/ui/page-loader"
import { ConversationDateSeparator } from "@/components/ui/conversation/conversation-date-separator"
import { ConversationEventLine } from "@/components/ui/conversation/conversation-events"
import { ConversationHeader, HEADER_CONTROL_STYLES } from "@/components/ui/conversation/conversation-header"
import { ConversationComposer } from "@/components/ui/conversation/conversation-composer"
import { ConfirmActionDialog } from "@/components/ui/conversation/confirm-action-dialog"
import { OutcomeCorrectionDialog } from "@/components/ui/conversation/outcome-correction-dialog"
import { AudioPlayer } from "@/components/ui/media/audio-player"
import {
  useSendConversationMessageMutation,
  useThreadActionMutation,
} from "@/hooks/mutations/unified-inbox"
import { useReplyToEmailThreadMutation } from "@/hooks/mutations/email-reply"
import { usePermissions } from "@/hooks/use-permissions"
import {
  EMAIL_PERMISSIONS,
  MESSAGING_AGENTS_PERMISSIONS,
} from "@/lib/auth/permissions.constants"
import { groupByDayInZone } from "@/lib/conversation/time-format"
import { idOfScopedValue, parseThreadKey } from "@/lib/inbox/scoped-id"
import { isThreadMutation, type ThreadAction, type ThreadMutation } from "@/lib/conversation/thread-header"
import type { UnifiedInboxItem, UnifiedThread } from "@/lib/inbox/types"
import { InboxThreadMessageBubble } from "./inbox-thread-message"
import { useTranslations } from "next-intl"


/** Wide enough that a partly-scrolled last line still counts as "watching the end". */
const BOTTOM_THRESHOLD_PX = 80

function pinToBottom(container: HTMLDivElement | null): void {
  if (container) container.scrollTop = container.scrollHeight
}

export interface AgentInboxLink {
  href: string | null
  /** Why `href` is null, shown under the transcript so the absence reads as a fact. */
  missingReason: string
}

interface InboxThreadPaneProps {
  /**
   * Null when the selected conversation is not in the loaded list — a shared
   * link to an older one, or one the current filters exclude. The thread still
   * loads, and so do its actions: they are performed against `thread.agent`,
   * which is why they do not depend on this.
   */
  item: UnifiedInboxItem | null
  thread: UnifiedThread | null | undefined
  isLoading: boolean
  error: Error | null
  timezone: string
  /** Shown on narrow screens, where opening a thread replaces the list. */
  onBack?: () => void
  /**
   * Where this conversation opens in the owning agent's own inbox. Absent on a
   * surface that IS the agent inbox — it then renders neither the link nor the
   * note explaining why there is none.
   */
  agentInboxLink?: AgentInboxLink
  /**
   * The one header action that opens something instead of writing: the surface
   * owns the review dialog, this only says which reply. Every surface that shows
   * email threads passes it.
   */
  onQaFeedback?: (autoReplyId: string) => void
}

/**
 * The working half of the inbox: read the conversation, and act on it without
 * leaving. What can be acted on is declared by the source and filtered by the
 * caller's permissions, so a header with no action bar means exactly that —
 * nothing here is offered and refused.
 */
export function InboxThreadPane({
  item,
  thread,
  isLoading,
  error,
  timezone,
  onBack,
  agentInboxLink,
  onQaFeedback,
}: InboxThreadPaneProps) {
  const tCopy = useTranslations("ConversationCopy")
  const tUi = useTranslations("Feedback.InboxThreadPane")

  const [pending, setPending] = useState<ThreadAction | null>(null)
  const [confirming, setConfirming] = useState<ThreadMutation | null>(null)
  const [correctingOutcome, setCorrectingOutcome] = useState(false)
  const { mutateAsync: performAction } = useThreadActionMutation()
  const { mutateAsync: sendMessage } = useSendConversationMessageMutation()
  const { mutateAsync: replyToEmail } = useReplyToEmailThreadMutation()
  const { can } = usePermissions()

  // Entries run oldest-first, so a thread longer than the pane opens on the oldest
  // of the loaded messages. A send forces the follow back on: the pane has no
  // success toast, so a reply that stayed below the fold would look like nothing
  // happened.
  const transcriptRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const scrolledThread = useRef<string | undefined>(undefined)
  // Not the entry count: past THREAD_MESSAGE_LIMIT a new message drops the oldest,
  // so the length stops moving exactly where the poll matters most.
  const newestEntryId = thread?.entries.at(-1)?.id
  // Recorded on scroll, not measured later: by the time the layout effect runs the
  // new entry has rendered and the container is never at the bottom any more.
  const wasAtBottom = useRef(true)
  useEffect(() => {
    const container = transcriptRef.current
    if (!container) return
    const record = () => {
      wasAtBottom.current =
        container.scrollHeight - container.scrollTop - container.clientHeight <= BOTTOM_THRESHOLD_PX
    }
    container.addEventListener("scroll", record, { passive: true })
    return () => container.removeEventListener("scroll", record)
  }, [])

  useLayoutEffect(() => {
    if (thread?.id !== scrolledThread.current) {
      scrolledThread.current = thread?.id
      wasAtBottom.current = true
    } else if (!wasAtBottom.current) {
      return
    }
    pinToBottom(transcriptRef.current)
  }, [thread?.id, newestEntryId])

  // The height at that moment is not the final one: a mail body's images have no
  // size until they load, and a swapped web font reflows every bubble. Both grow
  // the transcript AFTER the scroll, which is how an opened conversation ends up
  // showing its oldest message.
  useEffect(() => {
    const content = contentRef.current
    if (!content || typeof ResizeObserver === "undefined") return

    const observer = new ResizeObserver(() => {
      if (wasAtBottom.current) pinToBottom(transcriptRef.current)
    })
    observer.observe(content)
    return () => observer.disconnect()
  }, [])

  /**
   * The operator's own message brings the transcript back down — but only once it
   * has actually landed. A rejection, or an email outcome that keeps the draft
   * because nobody knows whether the mail left, appended nothing to follow.
   */
  const followOnSend = async <T,>(sending: Promise<T>): Promise<T> => {
    const result = await sending
    if (!(result as { keepDraft?: boolean } | null)?.keepDraft) wasAtBottom.current = true
    return result
  }

  /** Opened on the surface or performed here; a confirmable one asks first. */
  function dispatch(action: ThreadAction) {
    if (!isThreadMutation(action)) {
      const replyId = thread?.header.target?.autoReplyId
      if (replyId) onQaFeedback?.(replyId)
      return
    }
    if (action.confirm) setConfirming(action)
    else void run(action, tUi)
  }

  async function run(action: ThreadMutation, tUi: ReturnType<typeof useTranslations<"Feedback.InboxThreadPane">>) {
    // No agent means no route to perform it against — a shared mailbox has no
    // single owner. The source should not have offered the action at all, so say
    // so rather than swallowing the press.
    if (!thread?.agent) {
      toast.error(tUi("thisConversationHasNoOwningAgentToPerform"))
      return
    }

    setPending(action)
    try {
      await performAction({
        action,
        threadId: thread.id,
        agentId: thread.agent.id,
        nativeId: idOfScopedValue(thread.id),
        target: thread.header.target,
        // Only classification reads content, and the newest message is what the
        // header's `target` points at — so they must be the same message.
        content: classifiableContent(thread),
      })
    } catch {
      // The mutation already reported it; the pane only stops spinning.
    } finally {
      setPending(null)
    }
  }

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col">
      <ConversationHeader
        onBack={onBack}
        avatarUrl={item?.contact.avatarUrl ?? null}
        // The thread's own title first, for the same reason its channel comes first:
        // the row is absent for every conversation reached by a link or a reload.
        title={(thread ? localizeConversationDisplay(thread, tCopy).contactTitle : undefined) ?? (item ? localizeConversationDisplay(item.contact, tCopy).title : undefined) ?? tUi("conversation")}
        // The thread's own channel first: the row is absent for every conversation
        // reached by a link or a reload, and `source` is not a channel key.
        channelKind={thread?.channel.kind ?? item?.channel.kind ?? null}
        identity={(thread?.header.identityLines ?? []).map((line) => (
          <p key={line} className="truncate text-xs text-muted-foreground">
            {line}
          </p>
        ))}
        facts={thread?.header.facts}
        state={thread?.header.state ?? []}
        actions={thread?.header.actions ?? []}
        banners={thread?.header.banners}
        quality={thread?.header.quality}
        onCorrectQuality={
          can(MESSAGING_AGENTS_PERMISSIONS.MANAGE) ? () => setCorrectingOutcome(true) : undefined
        }
        pendingActionId={pending?.id ?? null}
        onAction={dispatch}
      >
        {agentInboxLink && <AgentInboxButton link={agentInboxLink} />}
      </ConversationHeader>

      {thread?.header.quality && (
        <OutcomeCorrectionDialog
          quality={thread.header.quality}
          open={correctingOutcome}
          onOpenChange={setCorrectingOutcome}
        />
      )}

      {thread?.header.recording && (
        // Under the header, and nothing at all when the call has no audio: an
        // empty player is worse than no player.
        <div className="flex-shrink-0 border-b p-4">
          <AudioPlayer
            audioUrl={thread.header.recording.url}
            conversationId={thread.header.recording.conversationId}
            callId={thread.header.recording.callId}
            initialDuration={thread.header.recording.durationSeconds}
          />
        </div>
      )}

      <div ref={transcriptRef} data-testid="transcript" className="flex-1 overflow-y-auto">
        <div ref={contentRef} className="space-y-6 p-4">
        {isLoading && <PageLoader />}

        {error && !isLoading && <p className="text-sm text-destructive"><UiErrorText message={error.message} /></p>}

        {!isLoading && !error && thread === null && (
          <p className="text-sm text-muted-foreground">
            <span key={tUi("thisConversationIsNoLongerAvailableItMay")}>{tUi("thisConversationIsNoLongerAvailableItMay")}</span></p>
        )}

        {thread?.hasOlder && (
          <p className="pb-2 text-center text-[11px] text-muted-foreground">
            <span><span key={tUi("showingTheMostRecentMessages")}>{tUi("showingTheMostRecentMessages")}</span></span>
            <span>{agentInboxLink?.href ? tUi("openItInTheAgentInboxForThe") : ""}</span>
          </p>
        )}

        {thread?.entries.length === 0 && (
          <p className="text-sm text-muted-foreground">
            <span key={tUi("nothingWasSaidInThisConversationThatCan")}>{tUi("nothingWasSaidInThisConversationThatCan")}</span></p>
        )}

        {groupByDayInZone(thread?.entries ?? [], timezone, (entry) => entry.timestamp).map(
          (group) => (
            <section key={group.key} className="space-y-5">
              <ConversationDateSeparator date={group.items[0].timestamp} timezone={timezone} />
              {group.items.map((entry) =>
                entry.kind === "message" ? (
                  <InboxThreadMessageBubble key={entry.id} message={entry} timezone={timezone} />
                ) : (
                  // No CRM link: the lead belongs to the agent inbox.
                  <ConversationEventLine key={entry.id} event={entry} />
                ),
              )}
              </section>
            ),
          )}
        </div>
      </div>

      {/* Mail, gated on the permission its own server action enforces. */}
      {thread?.source === "email" && can(EMAIL_PERMISSIONS.MANAGE) && (
        <ConversationComposer
          channelKind="email"
          placeholder={tUi("writeAReply")}
          // Absent for mail: a character counter is a channel limit, not one an
          // inbox has.
          identity={{ accountName: thread.channel.accountName }}
          pendingDraft={thread.header.pendingDraft}
          onSend={(text) => {
            // `email:<inboxId>:<threadId>`, and a thread id may itself hold colons
            // — `parseThreadKey` is the one place that split is written.
            const key = parseThreadKey(idOfScopedValue(thread.id))
            if (!key) throw new Error("This thread cannot be addressed for a reply.")
            return followOnSend(replyToEmail({ inboxId: key.inboxId, threadId: key.threadId, text }))
          }}
        />
      )}

      {/* Messaging only, and absent rather than disabled where a reply cannot go:
          a call has no channel to answer on, and the send route enforces
          `manage` — a composer a `view` operator cannot send from is a message
          they write and cannot deliver. */}
      {thread?.source === "message" &&
        thread.agent &&
        can(MESSAGING_AGENTS_PERMISSIONS.MANAGE) && (
        <ConversationComposer
          channelKind={thread.channel.kind}
          identity={{ accountName: thread.channel.accountName }}
          onSend={(text) =>
            followOnSend(
              sendMessage({
                agentId: thread.agent!.id,
                conversationId: idOfScopedValue(thread.id),
                text,
              }),
            )
          }
        />
      )}

      {agentInboxLink && !agentInboxLink.href && (
        <p className="flex-shrink-0 border-t px-4 py-2 text-[11px] text-muted-foreground">
          {agentInboxLink.missingReason}
        </p>
      )}

      <ConfirmActionDialog
        action={confirming}
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          if (confirming) void run(confirming, tUi)
          setConfirming(null)
        }}
      />
    </div>
  )
}

/**
 * An icon, not the widest control on the row: acting here no longer requires
 * leaving for the agent inbox. A real link, so it opens in a new tab and keeps
 * this one on the conversation being read.
 */
function AgentInboxButton({ link }: { link: AgentInboxLink }) {
  const tUi = useTranslations("Feedback.InboxThreadPane")

  if (!link.href) {
    return (
      <Button variant="ghost" size="icon" className={HEADER_CONTROL_STYLES.button} disabled title={link.missingReason}>
        <ExternalLink className="h-4 w-4" />
        <span className={HEADER_CONTROL_STYLES.label}><span key={tUi("openInAgentInbox")}>{tUi("openInAgentInbox")}</span></span>
      </Button>
    )
  }
  return (
    <Button variant="ghost" size="icon" className={HEADER_CONTROL_STYLES.button} asChild title={tUi("openInAgentInbox")}>
      <a href={link.href} target="_blank" rel="noopener noreferrer">
        <ExternalLink className="h-4 w-4" />
        <span className={HEADER_CONTROL_STYLES.label}><span key={tUi("openInAgentInbox")}>{tUi("openInAgentInbox")}</span></span>
      </a>
    </Button>
  )
}

/**
 * The newest CUSTOMER message — the same one `header.target` names. Classifying
 * the agent's own reply is what happens when these two disagree.
 */
function classifiableContent(thread: UnifiedThread): { text: string; subject?: string } | undefined {
  const inbound = [...thread.entries]
    .reverse()
    .find((entry) => entry.kind === "message" && entry.role === "customer")
  if (!inbound || inbound.kind !== "message") return undefined

  return { text: inbound.text, subject: thread.subject ?? undefined }
}
